import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Viewport width, in px, at or above which the sidebar sits in the document
 * flow. Below it the sidebar becomes an off-canvas drawer.
 *
 * Measured, not picked: with the fluid sidebar at its 224px floor and main's
 * p-6 padding (48px), the widest in-scope page content min-content is 236px
 * (/student/progress; /admin and /admin/broadcast are 232px). 224 + 48 + 236
 * = 501px is the exact width below which an in-flow sidebar forces horizontal
 * overflow. Rounded up to 520 for headroom.
 *
 * Keep in sync with the `min-[520px]:` / `max-[519px]:` Tailwind variants used
 * for anything that must switch without waiting on JS.
 */
export const SIDEBAR_BREAKPOINT = 520;

const COLLAPSED_KEY = 'inspire_sidebar_collapsed';

/**
 * Width-driven, not device-driven: matchMedia re-fires on browser resize,
 * split-screen, OS display scaling and monitor changes alike. No userAgent.
 */
export const useViewportAtLeast = (px) => {
  const query = `(min-width: ${px}px)`;
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

/**
 * Owns the two independent sidebar states:
 *  - isCollapsed: the desktop icon-only toggle. Persisted, only meaningful
 *    while inline.
 *  - isMobileOpen: the drawer. Never persisted, always starts closed, and
 *    closes on every route change.
 */
export const useSidebarController = () => {
  const isInline = useViewportAtLeast(SIDEBAR_BREAKPOINT);
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        /* private mode / storage disabled - collapse just stops persisting */
      }
      return next;
    });
  }, []);

  const openMobile = useCallback(() => setIsMobileOpen(true), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);

  // Drawer never survives a navigation.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMobileOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileOpen]);

  return {
    isInline,
    isCollapsed: isInline && isCollapsed,
    toggleCollapsed,
    isMobileOpen,
    openMobile,
    closeMobile,
  };
};
